import { useEffect } from 'react';

import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

type UseAutoScrollForReorderingArgs = {
	wrapperRef: React.RefObject<HTMLDivElement>;
	enableColumnReorder: boolean;
	enableRowReorder: boolean;
	leftPinnedColumnsWidth?: number;
	rightPinnedColumnsWidth?: number;
};

/**
 * Sets up autoscroll for column and row reordering during drag and drop operations.
 *
 * When there are pinned columns, we set up a custom scroll monitor that detects when
 * the cursor is near the edge of pinned columns and triggers scrolling. This is needed
 * because the standard autoscroll hitbox is at the container edge, but pinned columns
 * use position:sticky and visually overlay that edge, preventing the cursor from
 * reaching it.
 */
export const useAutoScrollForReordering = ({
	wrapperRef,
	enableColumnReorder,
	enableRowReorder,
	leftPinnedColumnsWidth = 0,
	rightPinnedColumnsWidth = 0,
}: UseAutoScrollForReorderingArgs) => {
	const hasLeftPinnedColumns = leftPinnedColumnsWidth > 0;
	const hasRightPinnedColumns = rightPinnedColumnsWidth > 0;
	const hasPinnedColumns = hasLeftPinnedColumns || hasRightPinnedColumns;

	useEffect(() => {
		if (!enableColumnReorder && !enableRowReorder) {
			return;
		}

		const wrapper = wrapperRef.current;
		if (!wrapper) {
			return;
		}

		const canScroll = ({ source }: { source: { data: { type?: string } } }) => {
			return source.data.type === 'item-column' || source.data.type === 'item-row';
		};

		// standard autoscroll handles scrolling when cursor reaches the actual container edge
		const standardAutoScroll = autoScrollForElements({
			element: wrapper,
			canScroll,
		});

		if (!hasPinnedColumns || !enableColumnReorder) {
			return standardAutoScroll;
		}

		let rafId: number | null = null;
		let scrollDirection: 'left' | 'right' | null = null;

		const scroll = () => {
			if (scrollDirection) {
				// 15px per frame is the standard autoscroll speed
				wrapper.scrollBy({ left: scrollDirection === 'left' ? -15 : 15 });
				rafId = requestAnimationFrame(scroll);
			}
		};

		const startScrolling = (direction: 'left' | 'right') => {
			if (scrollDirection === direction) {
				return;
			}
			stopScrolling();
			scrollDirection = direction;
			rafId = requestAnimationFrame(scroll);
		};

		const stopScrolling = () => {
			if (rafId) {
				cancelAnimationFrame(rafId);
				rafId = null;
			}
			scrollDirection = null;
		};

		const pinnedColumnScrollMonitor = monitorForElements({
			canMonitor: ({ source }) => source.data.type === 'item-column',
			onDrag: ({ location }) => {
				const { clientX, clientY } = location.current.input;
				const wrapperRect = wrapper.getBoundingClientRect();

				// check if cursor is within the wrapper
				if (clientY < wrapperRect.top || clientY > wrapperRect.bottom) {
					stopScrolling();
					return;
				}

				// check if cursor is near the right edge of left-pinned columns (scroll left)
				if (hasLeftPinnedColumns && wrapper.scrollLeft > 0) {
					const hitboxStart = wrapperRect.left + leftPinnedColumnsWidth;
					const hitboxEnd = hitboxStart + 60;
					if (clientX >= hitboxStart && clientX <= hitboxEnd) {
						startScrolling('left');
						return;
					}
				}

				// check if cursor is near the left edge of right-pinned columns (scroll right)
				if (hasRightPinnedColumns) {
					const canScrollRight = wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth;
					if (canScrollRight) {
						const hitboxEnd = wrapperRect.right - rightPinnedColumnsWidth;
						const hitboxStart = hitboxEnd - 60;
						if (clientX >= hitboxStart && clientX <= hitboxEnd) {
							startScrolling('right');
							return;
						}
					}
				}

				stopScrolling();
			},
			onDrop: stopScrolling,
		});

		return combine(standardAutoScroll, pinnedColumnScrollMonitor, stopScrolling);
	}, [
		enableColumnReorder,
		enableRowReorder,
		wrapperRef,
		hasPinnedColumns,
		hasLeftPinnedColumns,
		hasRightPinnedColumns,
		leftPinnedColumnsWidth,
		rightPinnedColumnsWidth,
	]);
};
